import { API } from 'aws-amplify';
import { t } from 'i18next';
import React, { useEffect, useMemo, useState, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Button, IconButton, Button as MuiButton, Typography } from '@mui/material';
import { HiOutlineTrash as DeleteIcon } from 'react-icons/hi';

import DataPlaceholder from '../shared/DataPlaceholder';
import Panel from '../shared/Panel';
import PanelSection from '../shared/PanelSection';
import StatementStatus from '../shared/StatementStatus';
import { ProtectedRouteContext } from '../Context';
import View from '../shared/View';
import MuiFooter from '../shared/muiControls/muiFooter';
import RenderIf from '../renderIf/RenderIf';
import DateTimeText from '../shared/DateTimeText';

import DatePicker from '../shared/datePick';
import { MuiStyledInput, MuiStyledSelect, RequiredMarker } from '../shared/muiControls';
import './AnnualStatements.scss';

import useCurrencyDisplay from '../../utils/useCurrencyDisplay';

import useFees from './annualStatements/useFees';
import ShipyardMenuButton from '../shared/shipyardMenu/ShipyardMenu';
import StatusChange from './annualStatements/statusChange';

function AnnualStatements(props) {
    const history = useHistory();

    const { userSettings } = useContext(ProtectedRouteContext);

    const [initialized, setInitialized] = useState(false);
    const [readOnly, setReadOnly] = useState(false);
    const [doingAjax, setDoingAjax] = useState(true);
    const [gradeList, setGradeList] = useState([]);
    const [statementInformation, setStatementInformation] = useState();
    const [autopop, setAutopop] = useState(false);
    const [displayStatusChange, setDisplayStatusChange] = useState(false);

    const {
        //State
        fees,
        feeTotal,
        discount,
        discountError,
        discountErrorMessage,
        showDiscountError,
        attendanceDate,
        dateError,
        dateErrorMessage,
        showDateError,
        isDoingAjax: feesDoingAjax,

        //Styles
        dateReadOnlySx,
        dateErrorTextSx,
        datePickTextFieldSx,
        dateLabelSx,
        selectSx,
        amountInputSx,
        descriptionInputSx,
        buttonBoxSx,
        autopopSx,
        discountAndTotalBoxSx,
        rowBoxSx,
        colBoxSx,

        //Handlers
        initialize,
        addFee,
        handleFeeDescription,
        handleDescriptionBlur,
        handleFeeAmount,
        handleAmountBlur,
        handleDiscountChange,
        handleDiscountBlur,
        handleAttendanceDateChange,
        handleAttendanceDateBlur,
        handleAttendanceDateError,
        saveRow,
        autopopFees,
        deleteFee,
        displayAllErrors,
    } = useFees();

    const { translateCurrency } = useCurrencyDisplay();

    const appId = props.match.params.applicationId;

    useEffect(() => {
        if (!initialized) {
            let retrieveData = async () => {
                setDoingAjax(true);
                try {
                    let response = await API.get('ShipyardAPI', `/finances/annual_statement/${appId}`, {
                        queryStringParameters: {
                            schoolId: history.location.state.SchoolID,
                        },
                    });

                    setGradeList(response.gradeList);

                    setStatementInformation(response.statementInformation[0]);

                    setAutopop(
                        response.gradeList.find((grade) => grade.GradeID === response.statementInformation[0].GradeLevelID).HasDefault === 1
                    );

                    if (response.statementInformation[0].StatementStatusID === 3 && userSettings.UserType !== 3) setReadOnly(true);
                    else if (response.statementInformation[0].StatementStatusID === 4) setReadOnly(true);
                    else setReadOnly(false);

                    initialize(response?.fees, response?.statementInformation[0]?.Discounts, response.statementInformation[0].EntryDate);

                    setInitialized(true);
                } catch (e) {
                    toast.error(t('General.AjaxError'));
                }
                setDoingAjax(false);
            };

            retrieveData();
        }
    }, [initialized, appId, userSettings]);

    const annualStatementsTitle = useMemo(() => {
        return !!statementInformation?.FirstName && !!statementInformation.LastName ? (
            <h2 className="annual-statements-title">
                <ReactMarkdown>
                    {t('AnnualStatements.Title', { StudentName: `${statementInformation.FirstName} ${statementInformation.LastName}` })}
                </ReactMarkdown>
            </h2>
        ) : (
            <h2 className="annual-statements-title">{t('AnnualStatements.LoadingTitle')}</h2>
        );
    }, [statementInformation]);

    const handleSaveSubmit = useMemo(() => {
        return async (_type) => {
            let pageErrors =
                feeTotal <= 0 ||
                isNaN(feeTotal) ||
                fees.find((fee) => fee?.descriptionError === true || fee?.amountError === true) !== undefined ||
                dateError === true ||
                discountError === true;
            if (_type === 2 && pageErrors) {
                displayAllErrors?.();
                switch (true) {
                    case fees[0].Amount === 0:
                        toast.error('Please enter a tuition amount that is numeric and greater than $0.00.');
                        break;
                    case fees.find((fee) => fee?.amountError === true):
                        toast.error('Please enter fee amount(s) that are numeric and greater than $0.00.');
                        break;
                    case fees.find((fee) => fee?.descriptionError === true):
                        toast.error('Please enter a description for all fees.');
                        break;
                    case feeTotal <= 0 || isNaN(feeTotal):
                        toast.error('The total amount must be greater than $0.00.');
                        break;
                    default:
                        toast.error('The page contains an error, please review, correct and resubmit the request.');
                        break;
                }

                return;
            }

            try {
                setDoingAjax(true);

                await API.patch('ShipyardAPI', `/finances/annual_statement/${appId}`, {
                    body: {
                        type: _type,
                        gradeLevelId: statementInformation.GradeLevelID,
                        appInstStmtId: statementInformation.AppInstStmtID,
                        entryDate: attendanceDate?.toISOString?.() || '',
                        schoolId: statementInformation.SchoolID,
                    },
                });

                toast.success(_type === 2 ? t('General.Submitted') : t('General.ChangesSaved'));

                setInitialized(false);
            } catch (e) {
                toast.error(t('General.AjaxError'));

                setDoingAjax(false);
            }
        };
    }, [appId, fees, statementInformation, feeTotal, attendanceDate, dateError]);

    const handleBack = async () => {
        history.push({
            pathname: `/finances`,
            state: {
                tabConfig: {
                    defaultView: 'annual',
                },
            },
        });
    };

    const handleStatusUpdated = (boolUpdated) => {
        setDisplayStatusChange(false);
        if (boolUpdated) {
            setInitialized(false);
        }
    };

    return (
        <View
            viewTitle={annualStatementsTitle}
            name="annual-statements"
            doingAjax={doingAjax}
        >
            <StatusChange
                open={displayStatusChange}
                handleClose={handleStatusUpdated}
                statusObj={<StatementStatus status={statementInformation?.StatementStatusID} />}
                statementId={statementInformation?.AppInstStmtID}
                currentStatus={statementInformation?.StatementStatusID}
            />

            <button
                className="back-button button-link"
                onClick={handleBack}
            >
                <FontAwesomeIcon icon={faArrowLeft} /> {t('AnnualStatements.BackButtonLabel')}
            </button>
            <Panel>
                <PanelSection
                    headerText="Status"
                    classes={['status-panel']}
                >
                    <div>
                        <span>{t('AnnualStatements.Status')}</span>
                        <span>
                            {!!statementInformation?.StatementStatusID ? (
                                <StatementStatus status={statementInformation.StatementStatusID} />
                            ) : (
                                <DataPlaceholder />
                            )}
                        </span>
                    </div>

                    <div>
                        <span>{t('AnnualStatements.SubmittedBy')}</span>
                        <span>
                            {!!statementInformation?.SubmittedFirstName && !!statementInformation?.SubmittedLastName ? (
                                `${statementInformation.SubmittedFirstName} ${statementInformation.SubmittedLastName}`
                            ) : statementInformation?.SubmittedFirstName === null &&
                              statementInformation?.SubmittedLastName === null ? null : (
                                <DataPlaceholder />
                            )}
                        </span>
                    </div>

                    <div>
                        <span>{t('AnnualStatements.SubmittedOn')}</span>
                        <span>
                            {!!statementInformation?.SubmittedUTC ? (
                                new Date(statementInformation.SubmittedUTC).toLocaleDateString()
                            ) : statementInformation?.SubmittedUTC === null ? (
                                '--/--/----'
                            ) : (
                                <DataPlaceholder />
                            )}
                        </span>
                    </div>
                </PanelSection>
                <PanelSection
                    headerText={t('AnnualStatements.StatementInformationHeader')}
                    classes={['details-panel']}
                >
                    <div>
                        <span>{t('AnnualStatements.StudentLabel')}:</span>
                        <span>
                            {!!statementInformation?.FirstName && !!statementInformation?.LastName ? (
                                `${statementInformation.FirstName} ${statementInformation.LastName}`
                            ) : (
                                <DataPlaceholder />
                            )}
                        </span>
                    </div>

                    <div>
                        <span>{t('AnnualStatements.DOBLabel')}:</span>
                        <span>
                            {!!statementInformation?.DateOfBirth ? (
                                <DateTimeText date={statementInformation.DateOfBirth} />
                            ) : (
                                <DataPlaceholder />
                            )}
                        </span>
                    </div>

                    <div>
                        <span>{t('AnnualStatements.StatementDateLabel')}</span>
                        <span>
                            {!!statementInformation ? <DateTimeText date={statementInformation.SubmittedUTC} /> : <DataPlaceholder />}
                        </span>
                    </div>

                    <div>
                        <span>{t('AnnualStatements.ParentGuardianLabel')}</span>
                        <span>
                            {!!statementInformation?.ParentFirstName && !!statementInformation?.ParentLastName ? (
                                `${statementInformation.ParentFirstName} ${statementInformation.ParentLastName}`
                            ) : (
                                <DataPlaceholder />
                            )}
                        </span>
                    </div>
                </PanelSection>
                <PanelSection headerText={t('AnnualStatements.DescriptionHeader')}>
                    <div className="description-text">
                        {initialized ? (
                            <ReactMarkdown className="tuition-instructions">{t('AnnualStatements.SummaryDescription')}</ReactMarkdown>
                        ) : (
                            <div>
                                <DataPlaceholder />
                            </div>
                        )}
                    </div>

                    <RenderIf isTrue={!initialized}>
                        {[...Array(5)].map((ent, i) => {
                            return (
                                <div>
                                    <DataPlaceholder />
                                </div>
                            );
                        })}
                    </RenderIf>

                    <RenderIf isTrue={initialized}>
                        {statementInformation?.GradeLevelID ? (
                            <Box
                                sx={(theme) => {
                                    return {
                                        mb: theme.spacing(1.25),
                                    };
                                }}
                            >
                                <MuiStyledSelect
                                    label={t('AnnualStatements.GradeLevelLabel')}
                                    value={statementInformation.GradeLevelID}
                                    disabled={readOnly || doingAjax || userSettings?.UserType === 2}
                                    onChange={(event) => {
                                        setStatementInformation((prev) => {
                                            prev.GradeLevelID = event.target.value;

                                            return {
                                                ...prev,
                                            };
                                        });

                                        setAutopop(
                                            gradeList.find((grade) => grade.GradeID === parseInt(event.target.value)).HasDefault === 1
                                        );
                                    }}
                                    selectSxObj={selectSx}
                                >
                                    {gradeList
                                        .sort((a, b) => {
                                            return a.Ordinal < b.Ordinal;
                                        })
                                        .map((grade) => {
                                            return {
                                                id: grade.GradeID,
                                                value: grade.GradeID,
                                                label: grade.Description,
                                            };
                                        })}
                                </MuiStyledSelect>
                            </Box>
                        ) : null}

                        {statementInformation?.StatementStatusID && attendanceDate !== undefined ? (
                            <Box
                                sx={(theme) => {
                                    return {
                                        mb: theme.spacing(1.25),
                                    };
                                }}
                            >
                                <Typography
                                    color={showDateError && dateError ? 'error' : undefined}
                                    sx={dateLabelSx}
                                >
                                    <RequiredMarker /> First Day of Attendance
                                </Typography>

                                <RenderIf isTrue={statementInformation.StatementStatusID !== 3}>
                                    <DatePicker
                                        size="small"
                                        clearable
                                        value={attendanceDate}
                                        disabled={readOnly || doingAjax}
                                        error={showDateError && dateError}
                                        textFieldSx={datePickTextFieldSx}
                                        onChange={(value) => handleAttendanceDateChange(value)}
                                        onBlur={handleAttendanceDateBlur}
                                        onError={handleAttendanceDateError}
                                        onClose={handleAttendanceDateBlur}
                                    />

                                    <RenderIf isTrue={showDateError && dateErrorMessage}>
                                        <Typography
                                            color="error"
                                            sx={dateErrorTextSx}
                                        >
                                            {dateErrorMessage}
                                        </Typography>
                                    </RenderIf>
                                </RenderIf>

                                <RenderIf isTrue={statementInformation.StatementStatusID === 3}>
                                    <Typography sx={dateReadOnlySx}>{attendanceDate?.format?.('MM/DD/YYYY')}</Typography>
                                </RenderIf>
                            </Box>
                        ) : null}

                        <Box sx={colBoxSx}>
                            {fees?.map?.((fee, i) => {
                                return (
                                    <Box
                                        sx={rowBoxSx}
                                        onBlur={(event) => saveRow(event, fee.AppInstStmtFeeID, statementInformation.AppInstStmtID)}
                                    >
                                        <MuiStyledInput
                                            label={i === 0 ? 'Description' : undefined}
                                            value={fee?.Description}
                                            disabled={i === 0 || readOnly || feesDoingAjax || doingAjax}
                                            onChange={(event) => handleFeeDescription(fee.AppInstStmtFeeID, event.target.value)}
                                            onBlur={() => handleDescriptionBlur(fee?.AppInstStmtFeeID)}
                                            error={fee?.showDescriptionError && fee?.descriptionError === true}
                                            errorMessage={fee?.showDescriptionError && fee?.descriptionErrorMessage}
                                            sx={descriptionInputSx}
                                        />

                                        <MuiStyledInput
                                            label={i === 0 ? 'Amount' : undefined}
                                            value={translateCurrency(fee?.Amount)?.replace('$', '')}
                                            disabled={readOnly || feesDoingAjax || doingAjax}
                                            onChange={(event) => handleFeeAmount(fee.AppInstStmtFeeID, event.target.value)}
                                            onBlur={() => handleAmountBlur(fee.AppInstStmtFeeID)}
                                            error={fee?.showAmountError === true && fee?.amountError === true}
                                            errorMessage={fee?.showAmountError === true && fee?.amountErrorMessage}
                                            sx={amountInputSx}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box
                                                        sx={(theme) => {
                                                            return {
                                                                width: theme.spacing(3),
                                                            };
                                                        }}
                                                    >
                                                        $
                                                    </Box>
                                                ),
                                            }}
                                        />

                                        <RenderIf isTrue={i !== 0 && !readOnly}>
                                            <IconButton
                                                color="error"
                                                sx={(theme) => {
                                                    return {
                                                        ml: theme.spacing(2),
                                                    };
                                                }}
                                                onClick={() =>
                                                    deleteFee(
                                                        statementInformation.AppInstStmtID,
                                                        fee.Ordinal,
                                                        appId,
                                                        statementInformation.SchoolID
                                                    )
                                                }
                                                disabled={feesDoingAjax || doingAjax}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </RenderIf>
                                    </Box>
                                );
                            })}

                            <RenderIf isTrue={!readOnly}>
                                <Box sx={buttonBoxSx}>
                                    <MuiButton
                                        onClick={() => addFee(history.location.state.SchoolID, appId)}
                                        disabled={feesDoingAjax || doingAjax}
                                    >
                                        + Add new fee
                                    </MuiButton>

                                    <RenderIf isTrue={userSettings?.UserType === 2}>
                                        <MuiButton
                                            variant="outlined"
                                            sx={autopopSx}
                                            disabled={feesDoingAjax || doingAjax || !autopop}
                                            onClick={() =>
                                                autopopFees(
                                                    autopop,
                                                    appId,
                                                    statementInformation.GradeLevelID,
                                                    history.location.state.SchoolID
                                                )
                                            }
                                        >
                                            Autopopulate Fees
                                        </MuiButton>
                                    </RenderIf>
                                </Box>
                            </RenderIf>
                        </Box>

                        <RenderIf isTrue={typeof statementInformation?.Discounts === 'number'}>
                            <Box sx={discountAndTotalBoxSx}>
                                <Box sx={rowBoxSx}>
                                    <MuiStyledInput
                                        label="Discounts and Total:"
                                        value="Discount(s)"
                                        disabled
                                        sx={descriptionInputSx}
                                    />

                                    <MuiStyledInput
                                        label="Amount:"
                                        value={translateCurrency(discount)?.replace('$', '')?.replace('-', '')}
                                        disabled={readOnly || feesDoingAjax || doingAjax}
                                        onChange={(event) => handleDiscountChange(event.target.value)}
                                        onBlur={() => handleDiscountBlur(history.location.state.SchoolID, appId)}
                                        sx={amountInputSx}
                                        error={showDiscountError && discountError}
                                        errorMessage={showDiscountError && discountErrorMessage}
                                        InputProps={{
                                            startAdornment: (
                                                <Box
                                                    sx={(theme) => {
                                                        return {
                                                            width: theme.spacing(3),
                                                        };
                                                    }}
                                                >
                                                    -&nbsp;$
                                                </Box>
                                            ),
                                        }}
                                    />
                                </Box>

                                <Box sx={rowBoxSx}>
                                    <MuiStyledInput
                                        value="Total Annual Tuition + Fees + Discount(s)"
                                        disabled
                                        sx={descriptionInputSx}
                                    />

                                    <MuiStyledInput
                                        value={translateCurrency(feeTotal)?.replace('$', '')}
                                        sx={amountInputSx}
                                        InputProps={{
                                            startAdornment: (
                                                <Box
                                                    sx={(theme) => {
                                                        return {
                                                            width: theme.spacing(3),
                                                        };
                                                    }}
                                                >
                                                    $
                                                </Box>
                                            ),
                                        }}
                                        disabled
                                    />
                                </Box>
                            </Box>
                        </RenderIf>
                    </RenderIf>
                </PanelSection>
            </Panel>
            <MuiFooter
                rightContent={[
                    <RenderIf
                        key="menu-button"
                        isTrue={userSettings?.UserType > 2}
                    >
                        <ShipyardMenuButton
                            label="Select Action"
                            actions={[
                                {
                                    key: 'status-change-action',
                                    label: 'Edit Status',
                                    action: () => setDisplayStatusChange(true),
                                },
                            ]}
                            disabled={doingAjax}
                        />
                    </RenderIf>,
                    <RenderIf
                        key="submit-button"
                        isTrue={userSettings?.UserType === 2}
                    >
                        <Button
                            id="submit-button"
                            onClick={() => handleSaveSubmit(2)}
                            disabled={doingAjax || statementInformation?.StatementStatusID > 2}
                            variant="contained"
                        >
                            {t('AnnualStatements.SubmitButton')}
                        </Button>
                    </RenderIf>,
                    <Button
                        key="save-button"
                        id="save-button"
                        onClick={() => handleSaveSubmit(1)}
                        disabled={doingAjax || (userSettings?.UserType === 2 && statementInformation?.StatementStatusID > 2)}
                        variant="contained"
                    >
                        {t('AnnualStatements.SaveButton')}
                    </Button>,
                ]}
                paperSx={{
                    padding: '1rem',
                }}
            />
        </View>
    );
}

export default AnnualStatements;
